import { useEffect, useRef, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { CloseOutlined, OpenInFullOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CustomFieldAppearance } from './customFieldAppearance';
import { CustomField } from '../../../../../ts/CustomFieldType';
import { customFieldIcon } from './customFieldIcons';

interface CustomFieldCardProps {
    label: string;
    value?: CustomField['Value'];
    appearance: CustomFieldAppearance;
    columns?: { xs?: number; sm?: number; md?: number };
}

export default function CustomFieldCard(props: CustomFieldCardProps) {
    const { label, value, appearance, columns } = props;
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [truncated, setTruncated] = useState(false);
    const valueRef = useRef<HTMLSpanElement | null>(null);
    const { color, iconName, fieldType } = appearance;

    const isBoolean = Number(fieldType) === 2 || typeof value === 'boolean';
    const booleanValue = value === true || value === 'true';
    const hasValue = value != null && String(value).trim() !== '';

    const displayValue = isBoolean
        ? (booleanValue ? t('control.centerControl.customFieldDetails.yes') : t('control.centerControl.customFieldDetails.no'))
        : hasValue ? String(value) : '\u2014';

    useEffect(() => {
        const measure = () => {
            const element = valueRef.current;
            if (element) setTruncated(element.scrollWidth > element.clientWidth + 1);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [displayValue]);

    return (
        <Grid item xs={columns?.xs ?? 12} sm={columns?.sm ?? 6} md={columns?.md ?? 4}>
            <Box
                sx={{
                    alignItems: 'center',
                    backgroundColor: 'var(--spectre-file-card-surface)',
                    border: '1px solid var(--spectre-border)',
                    borderRadius: 1,
                    display: 'flex',
                    gap: 1.5,
                    height: 76,
                    px: 1.5,
                    py: 1.25,
                    textAlign: 'left',
                }}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        backgroundColor: `${color}18`,
                        border: `1px solid ${color}55`,
                        borderRadius: 1,
                        color: color,
                        display: 'flex',
                        flex: '0 0 auto',
                        height: 46,
                        justifyContent: 'center',
                        width: 46,
                    }}
                >
                    {customFieldIcon(iconName, fieldType)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography color="text.secondary" noWrap title={label} sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                        {label}
                    </Typography>
                    <Typography
                        ref={valueRef}
                        component="span"
                        color={hasValue || isBoolean ? 'text.primary' : 'text.secondary'}
                        fontWeight={hasValue || isBoolean ? 600 : 400}
                        noWrap
                        title={displayValue}
                        sx={{ display: 'block' }}
                    >
                        {displayValue}
                    </Typography>
                </Box>
                {truncated && (
                    <Tooltip title={t('control.centerControl.customFieldDetails.viewFull')}>
                        <IconButton size="small" onClick={() => setExpanded(true)} sx={{ flex: '0 0 auto' }}>
                            <OpenInFullOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            <Dialog open={expanded} onClose={() => setExpanded(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ pr: 6 }}>
                    {label}
                    <IconButton aria-label="close" onClick={() => setExpanded(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseOutlined />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{displayValue}</Typography>
                </DialogContent>
            </Dialog>
        </Grid>
    );
}
